/**
 * Active Dropships presentation helpers — PURE, unit-testable functions for the
 * status badge mapping, carrier display labels, and the refresh-summary line.
 * The server owns status transitions; this only drives how they render.
 */
import type { ActiveStatus, RefreshSummary } from '@/lib/schemas/active'

export interface StatusBadge {
  label: string
  /** Tailwind border/bg/text classes for the badge chip. */
  className: string
}

const STATUS_BADGES: Record<ActiveStatus, StatusBadge> = {
  SUBMITTED: {
    label: 'Submitted — awaiting tracking',
    className: 'border-blue-300 bg-blue-50 text-blue-800',
  },
  TRACKING_RECEIVED: {
    label: 'Tracking received',
    className: 'border-indigo-300 bg-indigo-50 text-indigo-800',
  },
  SHOPIFY_UPDATED: {
    label: 'Shopify updated',
    className: 'border-green-300 bg-green-50 text-green-800',
  },
  ERROR: {
    label: 'Error',
    className: 'border-red-300 bg-red-50 text-red-800',
  },
  OVERDUE: {
    label: 'Overdue — 7+ days',
    className: 'border-amber-300 bg-amber-50 text-amber-800',
  },
  CANCELLED: {
    label: 'Cancelled',
    className: 'border-gray-300 bg-gray-50 text-gray-600',
  },
  COMPLETED: {
    label: 'Completed',
    className: 'border-slate-300 bg-slate-50 text-slate-700',
  },
}

export function statusBadge(status: ActiveStatus): StatusBadge {
  return STATUS_BADGES[status]
}

/** Retired = off the working board (cancelled or completed). Retired rows are
 * archival, so the board renders them read-only. */
export function isRetired(status: ActiveStatus): boolean {
  return status === 'CANCELLED' || status === 'COMPLETED'
}

export interface CarrierOption {
  value: string
  label: string
}

/** Manual-entry carrier dropdown options (spec Screen 3). */
export const CARRIERS: CarrierOption[] = [
  { value: 'FedEx', label: 'FedEx' },
  { value: 'UPS', label: 'UPS' },
  { value: 'USPS', label: 'USPS' },
  { value: 'DHL', label: 'DHL' },
  { value: 'Other', label: 'Other' },
]

const CARRIER_LABELS: Record<string, string> = {
  fedex: 'FedEx',
  ups: 'UPS',
  usps: 'USPS',
  dhl: 'DHL',
  other: 'Other',
}

/** Display label for a tracking's transporter code (e.g. "fedex" -> "FedEx"). */
export function carrierLabel(transporter: string): string {
  const key = transporter.trim().toLowerCase()
  return CARRIER_LABELS[key] ?? transporter
}

/** Carrier tracking-page URL builders, keyed by a substring of the transporter. */
const TRACKING_URLS: { match: string; build: (n: string) => string }[] = [
  { match: 'fedex', build: (n) => `https://www.fedex.com/wtrk/track/?trknbr=${n}` },
  { match: 'ups', build: (n) => `https://www.ups.com/track?tracknum=${n}` },
  { match: 'usps', build: (n) => `https://tools.usps.com/go/TrackConfirmAction?tLabels=${n}` },
  { match: 'dhl', build: (n) => `https://www.dhl.com/us-en/home/tracking.html?tracking-id=${n}` },
  { match: 'gls', build: (n) => `https://gls-group.eu/track/${n}` },
  { match: 'dpd', build: (n) => `https://tracking.dpd.de/status/en_US/parcel/${n}` },
  { match: 'bpost', build: (n) => `https://track.bpost.cloud/btr/web/#/search?itemCode=${n}` },
  { match: 'postnl', build: (n) => `https://jouw.postnl.nl/track-and-trace/${n}` },
]

/**
 * Carrier tracking-page URL for a transporter + tracking number, or null when the
 * transporter is empty/unknown. Matches the transporter case-insensitively as a
 * substring so "FedEx Ground" still resolves to FedEx. The tracking number is
 * URL-encoded.
 */
export function trackingUrl(transporter: string, trackingNumber: string): string | null {
  const key = transporter.trim().toLowerCase()
  if (key === '') return null
  const entry = TRACKING_URLS.find((e) => key.includes(e.match))
  if (!entry) return null
  return entry.build(encodeURIComponent(trackingNumber))
}

/**
 * One-line summary of a refresh-tracking run, e.g.
 * "Checked 3 · 1 tracking received · 1 posted to Shopify · 0 overdue · 0 errors".
 */
export function formatRefreshSummary(s: RefreshSummary): string {
  return [
    `Checked ${s.checked}`,
    `${s.tracking_received} tracking received`,
    `${s.shopify_updated} posted to Shopify`,
    `${s.overdue_flagged} overdue`,
    `${s.errors} errors`,
  ].join(' · ')
}
